import React, { useState, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { useTranslation } from 'react-i18next';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  List as BulletIcon,
  ListOrdered as OrderedIcon,
  Link2 as LinkIcon,
  Heading2 as H2Icon,
  Heading3 as H3Icon,
} from 'lucide-react';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const FieldShell = styled.div`
  position: relative;
  border: 1.5px solid ${({ theme, $error, $focused }) =>
    $error ? theme.colors.auxiliar.danger : $focused ? theme.colors.primary.main : '#E0E4E8'};
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  ${({ $focused }) => $focused && css`box-shadow: 0 0 0 3px rgba(2, 145, 239, 0.1);`}
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px 10px;
  border-bottom: 1px solid #EEF1F4;
  background: #FAFBFC;
  border-radius: ${({ theme }) => theme.metrics.radius.medium} ${({ theme }) => theme.metrics.radius.medium} 0 0;
`;

const ToolButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 6px;
  background: ${({ $active, theme }) => $active ? theme.colors.primary.main : 'transparent'};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.text.dark.little};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  &:hover:not(:disabled) {
    background: ${({ $active, theme }) => $active ? theme.colors.primary.main : '#E8F4FD'};
    color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.primary.main};
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const Separator = styled.span`
  width: 1px;
  background: #E0E4E8;
  margin: 4px 4px;
`;

const EditorArea = styled.div`
  padding: 14px 16px;
  min-height: ${({ $minRows }) => ($minRows || 4) * 24}px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: text;

  .ProseMirror {
    outline: none;
    min-height: ${({ $minRows }) => Math.max(($minRows || 4) - 1, 2) * 24}px;
    line-height: 1.55;
  }
  .ProseMirror p { margin: 0 0 8px 0; }
  .ProseMirror p:last-child { margin-bottom: 0; }
  .ProseMirror ul, .ProseMirror ol { margin: 0 0 8px 0; padding-left: 24px !important; }
  .ProseMirror ul { list-style: disc outside !important; }
  .ProseMirror ol { list-style: decimal outside !important; }
  .ProseMirror ul li, .ProseMirror ol li { margin: 2px 0; display: list-item; }
  .ProseMirror ul ul { list-style: circle outside !important; }
  .ProseMirror ul ul ul { list-style: square outside !important; }
  .ProseMirror h2 { font-size: 16px; font-weight: 700; margin: 6px 0 6px 0; }
  .ProseMirror h3 { font-size: 14px; font-weight: 700; margin: 4px 0 4px 0; }
  .ProseMirror a {
    color: ${({ theme }) => theme.colors.primary.main};
    text-decoration: underline;
  }
  .ProseMirror p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: #A0A8B4;
    pointer-events: none;
    height: 0;
  }
`;

const FloatingLabel = styled.label`
  position: absolute;
  left: 14px;
  top: -8px;
  font-size: 11px;
  background: #fff;
  padding: 0 4px;
  pointer-events: none;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme, $error }) => $error ? theme.colors.auxiliar.danger : theme.colors.primary.main};
  z-index: 1;
`;

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.auxiliar.danger};
  margin-left: 2px;
`;

const ErrorText = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.auxiliar.danger};
`;

// TipTap emits "<p></p>" for an empty doc — normalize to "" so required-field
// validation and BE payloads stay honest.
const isEmptyHtml = (html) => {
  if (!html) return true;
  const stripped = html.replace(/<p>\s*<\/p>/g, '').replace(/<br\s*\/?>/g, '').trim();
  return stripped === '';
};

export default function RichTextInput({
  label,
  value,
  onChange,
  error,
  required = false,
  rows = 6,
  placeholder,
}) {
  const { t } = useTranslation();
  const [focused, setFocused] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        // Drop features we deliberately don't expose in the toolbar.
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        code: false,
        strike: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
      }),
      Placeholder.configure({ placeholder: placeholder || '' }),
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      onChange?.(isEmptyHtml(html) ? '' : html);
    },
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
  });

  // Re-sync when the controlling form swaps the value externally
  // (drawer opens for a different record). Avoid clobbering during typing.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || '';
    if (isEmptyHtml(current) && isEmptyHtml(next)) return;
    if (current === next) return;
    editor.commands.setContent(next, false);
  }, [value, editor]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href;
    // eslint-disable-next-line no-alert
    const url = window.prompt(t('richText.linkPrompt'), prev || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor, t]);

  if (!editor) return null;

  return (
    <Wrapper>
      <FieldShell $error={!!error} $focused={focused}>
        <Toolbar onMouseDown={(e) => e.preventDefault()}>
          <ToolButton
            type="button"
            $active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
            title={t('richText.bold')}
          >
            <BoldIcon size={16} />
          </ToolButton>
          <ToolButton
            type="button"
            $active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            title={t('richText.italic')}
          >
            <ItalicIcon size={16} />
          </ToolButton>
          <Separator />
          <ToolButton
            type="button"
            $active={editor.isActive('heading', { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            title={t('richText.heading2')}
          >
            <H2Icon size={16} />
          </ToolButton>
          <ToolButton
            type="button"
            $active={editor.isActive('heading', { level: 3 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            title={t('richText.heading3')}
          >
            <H3Icon size={16} />
          </ToolButton>
          <Separator />
          <ToolButton
            type="button"
            $active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            title={t('richText.bulletList')}
          >
            <BulletIcon size={16} />
          </ToolButton>
          <ToolButton
            type="button"
            $active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            title={t('richText.orderedList')}
          >
            <OrderedIcon size={16} />
          </ToolButton>
          <Separator />
          <ToolButton
            type="button"
            $active={editor.isActive('link')}
            onClick={addLink}
            title={t('richText.link')}
          >
            <LinkIcon size={16} />
          </ToolButton>
        </Toolbar>
        <EditorArea $minRows={rows} onClick={() => editor.commands.focus()}>
          <EditorContent editor={editor} />
        </EditorArea>
      </FieldShell>
      {label && (
        <FloatingLabel $error={!!error}>
          {label}
          {required && <RequiredMark>*</RequiredMark>}
        </FloatingLabel>
      )}
      {error && <ErrorText>{error}</ErrorText>}
    </Wrapper>
  );
}
